import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    // Estrutura e tipografia
    'relative inline-flex items-center justify-center overflow-hidden align-middle',
    'cursor-pointer border-0 whitespace-nowrap select-none',
    'rounded-[var(--surface-rounder-pill)]',
    '[font-size:var(--font-size-scale-up-01)] [font-weight:var(--font-weight-semi-bold)]',
    'gap-[var(--spacing-scale-base)] px-[var(--spacing-scale-3x)]',
    'text-[var(--interactive)]',
    // Transição
    'transition-[background-image,background-color,color,border-color] [transition-duration:var(--duration-fast)]',
    // Foco visível: outline tracejado dourado com offset (padrão gov.br)
    'outline-none',
    'focus-visible:outline focus-visible:[outline-style:var(--focus-style)]',
    'focus-visible:[outline-width:var(--focus-width)] focus-visible:[outline-offset:var(--focus-offset)]',
    'focus-visible:[outline-color:var(--focus-color)]',
    // Hover/pressed por camada de sobreposição (rgba de --interactive-rgb).
    // O seletor é :not(:disabled) — e não `enabled:` — porque `:enabled` só
    // casa com controles de formulário: com `asChild` sobre um <a>, o botão
    // ficaria sem hover.
    '[&:not(:disabled):hover]:[background-image:linear-gradient(rgba(var(--interactive-rgb),var(--hover)),rgba(var(--interactive-rgb),var(--hover)))]',
    '[&:not(:disabled):active]:[background-image:linear-gradient(rgba(var(--interactive-rgb),var(--pressed)),rgba(var(--interactive-rgb),var(--pressed)))]',
    // Desabilitado
    'disabled:cursor-not-allowed disabled:[opacity:var(--disabled)]',
    // Ícones (Lucide via <svg> ou Font Awesome via .svg-inline--fa)
    '[&_.svg-inline--fa]:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        // Base transparente (botão de texto/terciário do DS)
        tertiary: 'bg-transparent',
        // Fundos sólidos escuros: a sobreposição de hover/pressed é branca, e o
        // UIKIT a desenha nas opacidades "dark" (30% e 65%) — nas claras (16% e
        // 45%) o estado quase não aparece sobre o azul.
        primary: [
          'bg-[var(--interactive-light)] text-[var(--color-dark)] [--interactive-rgb:var(--color-dark-rgb)]',
          '[--hover:var(--hover-dark)] [--pressed:var(--pressed-dark)]',
        ].join(' '),
        secondary:
          'border [border-width:var(--surface-width-sm)] [border-color:var(--interactive)] bg-[var(--background-light)] text-[var(--interactive)]',
        danger: [
          'bg-[var(--danger)] text-[var(--color-dark)] [--interactive-rgb:var(--color-dark-rgb)]',
          '[--hover:var(--hover-dark)] [--pressed:var(--pressed-dark)]',
        ].join(' '),
        success: [
          'bg-[var(--success)] text-[var(--color-dark)] [--interactive-rgb:var(--color-dark-rgb)]',
          '[--hover:var(--hover-dark)] [--pressed:var(--pressed-dark)]',
        ].join(' '),
        // O amarelo leva sobreposição escura, que já marca bem nas claras.
        warning:
          'bg-[var(--warning)] text-[var(--color-light)] [--interactive-rgb:var(--color-light-rgb)]',
        info: [
          'bg-[var(--info)] text-[var(--color-dark)] [--interactive-rgb:var(--color-dark-rgb)]',
          '[--hover:var(--hover-dark)] [--pressed:var(--pressed-dark)]',
        ].join(' '),
      },
      size: {
        xsmall: 'h-[var(--spacing-scale-3x)]',
        small: 'h-[var(--spacing-scale-4x)]',
        medium: 'h-[var(--spacing-scale-5x)]',
        large: 'h-[var(--spacing-scale-6x)]',
      },
      block: { true: 'w-full' },
      circle: { true: 'aspect-square rounded-full px-0' },
      // .br-button.active: estado "actived" do UIKIT — fundo --active
      // (blue-warm-vivid-80) e conteúdo claro, seja qual for a ênfase.
      active: {
        true: [
          'border-transparent bg-[var(--active)] text-[var(--color-dark)]',
          '[--interactive-rgb:var(--color-dark-rgb)]',
          '[--hover:var(--hover-dark)] [--pressed:var(--pressed-dark)]',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
)

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** Exibe spinner, marca aria-busy e bloqueia interação. */
    loading?: boolean
  }

function Button({
  className,
  variant,
  size,
  block,
  circle,
  active,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button'
  const classes = cn(
    buttonVariants({ variant, size, block, circle, active, className }),
    loading && [
      'cursor-progress disabled:cursor-progress',
      '[&>[data-slot=button-content]]:opacity-0',
      // No UIKIT o estado "progress" mantém a cor cheia — quem esmaece é o
      // "disabled". Como carregar também bloqueia o clique via `disabled`,
      // desfazemos aqui só a opacidade.
      'disabled:opacity-100',
    ]
  )

  // asChild: delega o elemento; não injetamos spinner (Slot exige filho único).
  if (asChild) {
    return (
      <Comp data-slot="button" className={classes} {...props}>
        {children}
      </Comp>
    )
  }

  return (
    <button
      data-slot="button"
      className={classes}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span
          data-slot="button-spinner"
          aria-hidden="true"
          // Anel do DS: 24px de diâmetro, traço de 2px com o lado direito
          // vazado e uma volta a cada 1,3s. A cor vem de `currentColor` — que
          // já é branco nas ênfases de fundo sólido e azul nas demais, como o
          // core faz trocando a borda por variante.
          className={cn(
            'absolute size-[var(--spacing-scale-3x)] rounded-full',
            '[border-width:var(--surface-width-md)] border-current border-r-transparent',
            'animate-spin [animation-duration:1.3s] [animation-timing-function:linear]'
          )}
        />
      )}
      <span
        data-slot="button-content"
        className="inline-flex items-center gap-[var(--spacing-scale-base)]"
      >
        {children}
      </span>
    </button>
  )
}

export { Button, buttonVariants }
